import elevarCartProductList from '@/lib/elevar/cart-item-list';
import { ELEVAR_DEFAULT_CURRENCY_CODE } from '@/lib/elevar/constants';
import { fromGid } from '@/lib/shopify/gid';
import { toCents, toDollars } from '@/utils/utilities.mjs';

const normalizeProductType = (value) => String(value || '').trim().toLowerCase();

/** Reads a single cart line custom attribute by key. */
const findAttributeValue = (attributes, key) => {
  if (!Array.isArray(attributes)) return '';
  return attributes.find((attr) => attr?.key === key)?.value ?? '';
};

/** Prefer `_productType` cart attribute over Shopify `productType` when present. */
const findProductType = (attributes) =>
  normalizeProductType(findAttributeValue(attributes, '_productType'));

/** Shopify returns category ancestors leaf-to-root; reverse to build `"A > B > C"`. */
const buildCategoryPath = (category) => {
  if (!category) return null;

  const ancestors = category.ancestors || [];

  return [...ancestors.map((ancestor) => ancestor.name).reverse(), category.name]
    .filter(Boolean)
    .join(' > ');
};

/** Free-text metafield holding a Google taxonomy path, normalizes A>B to A > B. */
const readGoogleCategoryMetafield = (product) =>
  String(product?.googleCategory?.value ?? '')
    .split('>')
    .map((segment) => segment.trim())
    .filter(Boolean)
    .join(' > ');

/**
 * Google Product Category for Elevar: the `custom.google_category` metafield, else the
 * Shopify Category.
 */
const resolveElevarCategory = (product) => {
  const googleCategory = readGoogleCategoryMetafield(product);

  if (googleCategory) return googleCategory;

  const shopifyCategoryPath = buildCategoryPath(product?.category);

  if (process.env.NODE_ENV === 'development' && !shopifyCategoryPath) {
    console.warn(
      `[Elevar] No category for "${product?.handle || 'unknown product'}".`,
    );
  }

  return shopifyCategoryPath || null;
};

/** Collection list context for cart products (e.g. mattress → /collections/mattresses). */
const resolveCartListPath = (productType) => {
  const normalizedType = normalizeProductType(productType);

  return (
    elevarCartProductList.find((item) => item.type === normalizedType)?.list ??
    null
  );
};

/** Reads currencyCode from cart, cart line, or variant/merchandise money objects. */
const readElevarCurrencyCode = (source) =>
  source?.cost?.subtotalAmount?.currencyCode ||
  source?.cost?.totalAmount?.currencyCode ||
  source?.merchandise?.price?.currencyCode ||
  source?.price?.currencyCode ||
  null;

/** Returns Shopify currency or `ELEVAR_DEFAULT_CURRENCY_CODE` when money data is missing. */
const getElevarCurrencyCode = (source) =>
  readElevarCurrencyCode(source) || ELEVAR_DEFAULT_CURRENCY_CODE;

/** 
 * Runtime, SSR, and lazy-init guard. 
 * Elevar strictly requires window.ElevarDataLayer to be initialized before any push.
 */
const initElevarDataLayer = () => {
  if (typeof window === 'undefined') return false;
  window.ElevarDataLayer = window.ElevarDataLayer ?? [];
  return true;
};


/** Formats a Shopify money object, number, or string for Elevar (e.g. `"119.99"`). */
const getMoneyAmount = (moneyValue) => {
  const raw = moneyValue?.amount ?? moneyValue;
  if (raw == null || raw === '') return toDollars(toCents(0)).toFixed(2);
  const n = Number(raw);
  if (Number.isNaN(n)) return String(raw);
  return toDollars(toCents(n)).toFixed(2);
};

/**
 * Elevar `compare_at_price`: MSRP when the variant is discounted, otherwise
 * mirrors `price` so the two fields match when there's no active discount.
 */
const getCompareAtPriceAmount = (hasPriceDifference, full, price) =>
  hasPriceDifference ? getMoneyAmount(full) : getMoneyAmount(price);

/** Builds Elevar `user_properties` from the `userdata` localStorage entry. */
const getUserData = () => {
  if (typeof window === 'undefined') return { visitor_type: 'guest' };

  let custdata = null;

  try {
    custdata = JSON.parse(window.localStorage.getItem('userdata'));
  } catch {
    custdata = null;
  }

  if (custdata) {
    return {
      visitor_type: 'logged_in',
      customer_id: fromGid(custdata.id),
      customer_email: custdata.email,
    };
  }

  return { visitor_type: 'guest' };
};

export {
  findAttributeValue,
  findProductType,
  getCompareAtPriceAmount,
  getElevarCurrencyCode,
  getMoneyAmount,
  getUserData,
  initElevarDataLayer,
  resolveCartListPath,
  resolveElevarCategory,
};
